import { inject, Injectable, signal } from '@angular/core';
import { AdminMenuService } from '@core/components/menu/services/menu-admin.service';
import { ClientMenuService } from '@core/components/menu/services/menu-clients.service';
import { ConfigGeneralMenuService } from '@core/components/menu/services/menu-config-general.service';
import { ConfigurationMenuService } from '@core/components/menu/services/menu-configuration.service';
import { CustomerMenuService } from '@core/components/menu/services/menu-customers.service';
import { MasterLockMenuService } from '@core/components/menu/services/menu-master-lock.service';
import { MenuSsoService } from '@core/components/menu/services/menu-sso.service';
import { SupervisionMenuService } from '@core/components/menu/services/menu-supervision.service';
import { ValidatorMenuService } from '@core/components/menu/services/menu-validator.service';
import { AppRoutes } from '@core/routes/route.paths';
import { AppService } from '@core/services/app.service';
import { UserService } from '@core/services/user/user.service';
import { MasterlockService } from '@features/masterlock/services/masterlock.service';
import { AccessRightRoute } from '@shared/model/access-right.type';
import { MenuItem } from '@shared/model/menu-item.type';
import { BehaviorSubject } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class MenuService {
  private readonly userService = inject(UserService);
  private readonly appService = inject(AppService);
  private readonly customerMenuService = inject(CustomerMenuService);
  private readonly validatorMenuService = inject(ValidatorMenuService);
  private readonly configurationMenuService = inject(ConfigurationMenuService);
  private readonly configGeneralMenuService = inject(ConfigGeneralMenuService);
  private readonly adminMenuService = inject(AdminMenuService);
  private readonly clientMenuService = inject(ClientMenuService);
  private readonly supervisionMenuService = inject(SupervisionMenuService);
  private readonly masterLockMenuService = inject(MasterLockMenuService);
  private readonly masterlockService = inject(MasterlockService);
  private readonly ssoMenuService = inject(MenuSsoService);

  readonly subMenu = signal(null);
  readonly displayMenuLabel = signal(false);
  readonly displaySubMenu = signal(false);

  showMenu = new BehaviorSubject<boolean>(false);
  showMenu$ = this.showMenu.asObservable();

  toggleMenuAndLabel() {
    this.displayMenuLabel.update((state) => !state);
    this.displaySubMenu.update((state) => !state);
  }

  displaySubMenuAndMenuLabel() {
    this.displayMenuLabel.update(() => true);
    this.displaySubMenu.update(() => true);
  }

  updateShowMenu(group?: string) {
    if (group && (group.startsWith('fidal') || group.startsWith('ma_compagnie'))) {
      this.showMenu.next(false);
    } else {
      this.showMenu.next(!this.showMenu.getValue());
    }
  }

  setShowMenuTrue() {
    this.showMenu.next(true);
  }

  getMenu(): MenuItem[] {
    return [
      {
        routerLink: AppRoutes.Customers.root,
        iconType: 'icon',
        icon: 'pbi-users',
        label: 'main-menu.customers',
        show: () => this.userService.isSuperUser(),
        subMenu: this.customerMenuService.getMenu(),
      },
      {
        routerLink: this.userService.firstAccessibleValidatorRoute(),
        iconType: 'icon',
        icon: 'pbi-add-circle',
        label: 'main-menu.validator',
        show: () => this.userService.showValidators(),
        subMenu: this.validatorMenuService.getMenu(),
      },
      {
        routerLink: AppRoutes.VirtualPrinter,
        iconType: 'icon',
        icon: 'pbi-send',
        label: 'menu.shipment',
        show: () => this.userService.checkAccessRights(AccessRightRoute.VirtualPrinter),
      },
      {
        routerLink: AppRoutes.VirtualPrinterNew,
        iconType: 'icon',
        icon: 'pbi-printer',
        label: 'menu.shipment',
        show: () => this.userService.checkAccessRights(AccessRightRoute.VirtualPrinterNew),
      },
      {
        routerLink: AppRoutes.Report.root,
        iconType: 'icon',
        icon: 'pbi-icon-mini pbi-chart-bars-axis',
        label: 'main-menu.report',
        show: () => this.userService.checkAccessRights(AccessRightRoute.Report.root),
      },
      {
        routerLink: AppRoutes.Search,
        iconType: 'icon',
        icon: 'pbi-search',
        label: 'main-menu.search',
        show: () => this.userService.checkAccessRights(AccessRightRoute.Search),
      },
      {
        routerLink: AppRoutes.GenerixEmails,
        iconType: 'icon',
        icon: 'pbi-mail',
        label: 'main.messaging',
        show: () => this.userService.checkAccessRights(AccessRightRoute.GenerixEmails),
      },
      {
        routerLink: AppRoutes.SuivreEnvois,
        iconType: 'icon',
        icon: 'pbi-archive-drawer',
        label: 'menu.tracking',
        show: () => this.userService.checkAccessRights(AccessRightRoute.SuivreEnvois),
      },
      {
        routerLink: AppRoutes.AccesGed.root,
        iconType: 'icon',
        icon: 'pbi-catalog',
        label: 'menu.ged-access',
        show: () => this.userService.checkAccessRights(AccessRightRoute.AccesGed),
      },
      {
        routerLink: AppRoutes.MiseEnGed,
        iconType: 'icon',
        icon: 'pbi-catalog-new',
        label: 'menu.add-ged',
        show: () => this.userService.checkAccessRights(AccessRightRoute.MiseEnGed),
      },
      {
        routerLink: AppRoutes.ValiderPlis,
        iconType: 'icon',
        icon: 'pbi-check-square',
        label: 'menu.validation',
        show: () => this.userService.checkAccessRights(AccessRightRoute.ValiderPlis),
      },
      {
        routerLink: AppRoutes.InputValidator,
        iconType: 'icon',
        icon: 'pbi-check-circle',
        label: 'main.validate-drops',
        show: () => this.userService.checkAccessRights(AccessRightRoute.InputValidator),
      },
      {
        routerLink: AppRoutes.DashboardCourrier,
        iconType: 'icon',
        icon: 'pbi-icon-mini pbi-chart-bars-axis',
        label: 'menu.stats',
        show: () => this.userService.checkAccessRights(AccessRightRoute.DashboardCourrier),
      },
      {
        href: `https://${this.appService.options().clientLink}`,
        iconType: 'icon',
        icon: 'pbi-link',
        label: 'menu.customer-portal',
        show: () => !this.userService.isSuperUser() && this.appService.clientLinkExists(),
      },
      {
        routerLink: AppRoutes.Configuration.root,
        iconType: 'icon',
        icon: 'pbi-settings',
        label: 'main-menu.configuration',
        show: () => this.userService.isSuperUser(),
        subMenu: this.configurationMenuService.getMenu(),
      },
      {
        routerLink: AppRoutes.ConfigGeneral.root,
        iconType: 'icon',
        icon: 'pbi-preferences',
        label: 'menu.general-config',
        show: () => this.userService.isSuperUser(),
        subMenu: this.configGeneralMenuService.getMenu(),
      },
      {
        routerLink: AppRoutes.ConfigurationClient.ConfigurationSftp,
        iconType: 'icon',
        icon: 'pbi-icon-mini pbi-key',
        label: 'main-menu.config-sftp',
        show: () =>
          this.userService.checkAccessRights(AccessRightRoute.ConfigurationSftp) && !this.userService.isSuperUser(),
      },
      {
        routerLink: AppRoutes.Facturations,
        iconType: 'icon',
        icon: 'pbi-currency-euro',
        label: 'menu.billing',
        show: () => this.userService.isSuperUser(),
      },
      {
        routerLink: AppRoutes.Admin.root,
        iconType: 'icon',
        icon: 'pbi-folder',
        label: 'main-menu.admin',
        show: () => this.userService.isSuperUser(),
        subMenu: this.adminMenuService.getMenu(),
      },
      {
        routerLink: AppRoutes.Information,
        iconType: 'icon',
        icon: 'pbi-info-circle',
        label: 'main-menu.information',
        show: () => this.appService.clientLinkExists(),
      },
      {
        routerLink: AppRoutes.Clients.root,
        iconType: 'icon',
        icon: 'pbi-user-circle',
        label: 'main-menu.clients',
        subMenu: this.clientMenuService.getMenu(),
        show: () => this.appService.clientLinkExists(),
      },
      {
        routerLink: AppRoutes.Supervision.Global,
        iconType: 'icon',
        icon: 'pbi-monitor',
        label: 'main-menu.monitor',
        subMenu: this.supervisionMenuService.getMenu(),
        show: () => this.userService.isSuperUser(),
      },
      {
        routerLink: this.masterlockService.masterLockRoute(),
        iconType: 'icon',
        icon: 'pbi-file-content',
        label: 'main-menu.masterlock',
        subMenu: this.masterLockMenuService.getMenu(),
        show: () => this.userService.checkDatabaseAccess(),
      },
      {
        routerLink: AppRoutes.Sso.root,
        iconType: 'icon',
        icon: 'pbi-unlock',
        label: 'menu.sso',
        subMenu: this.ssoMenuService.getMenu(),
        show: () => this.userService.checkSsoAccess(),
      },
      {
        routerLink: AppRoutes.VirtuarlPrinterApp.root,
        iconType: 'icon',
        icon: 'pbi-download',
        label: 'core.header.printer-driver-app',
        show: () => this.userService.checkAccessRights(AccessRightRoute.VirtualPrinterApp),
      },
    ];
  }
}
